// ============================================================
// DIRECT WORK MODULE
// src/DirectWork.js
// ============================================================

import { useState, useEffect, useCallback, useRef } from "react";
import { createClient } from "@supabase/supabase-js";
import { fmtDMY } from "./dateUtils";
import { matchItems } from "./matchItems";

const SUPABASE_URL  = process.env.REACT_APP_SUPABASE_URL  || "https://ryokwpjntufianxwdyqz.supabase.co";
const SUPABASE_ANON = process.env.REACT_APP_SUPABASE_ANON || "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InJ5b2t3cGpudHVmaWFueHdkeXF6Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzY0MDk1MTcsImV4cCI6MjA5MTk4NTUxN30.V9Bs4-Uijww6cB1jS1HBqL1RIHfRgs4WbiYP_mVmkFA";
const supabase = createClient(SUPABASE_URL, SUPABASE_ANON);

const fmt = (n) => "₹" + parseFloat(n||0).toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2});

async function fetchAll(table, query = {}) {
  let all = [], from = 0;
  while (true) {
    let req = supabase.from(table).select(query.select||"*");
    if (query.eq)  Object.entries(query.eq).forEach(([k,v])=>{ req=req.eq(k,v); });
    if (query.neq) Object.entries(query.neq).forEach(([k,v])=>{ req=req.neq(k,v); });
    if (query.order) req = req.order(query.order, {ascending:query.ascending??true});
    req = req.range(from, from+999);
    const {data,error} = await req;
    if (error||!data||data.length===0) break;
    all = [...all,...data];
    if (data.length<1000) break;
    from += 1000;
  }
  return all;
}

const S = {
  pageHeader: {display:"flex",alignItems:"flex-end",justifyContent:"space-between",marginBottom:28,paddingBottom:20,borderBottom:"1px solid var(--paper3)"},
  pageTitle:  {fontFamily:"var(--serif)",fontSize:29,fontWeight:300,letterSpacing:"-0.5px"},
  subtitle:   {fontSize:12,color:"var(--ink3)",marginTop:6},
  btnPrimary: {display:"inline-flex",alignItems:"center",gap:6,padding:"8px 16px",borderRadius:10,fontFamily:"var(--mono)",fontSize:13,cursor:"pointer",border:"none",background:"var(--accent)",color:"#fff",fontWeight:500},
  btnGhost:   {display:"inline-flex",alignItems:"center",gap:6,padding:"8px 16px",borderRadius:10,fontFamily:"var(--mono)",fontSize:13,cursor:"pointer",border:"1px solid var(--paper3)",background:"transparent",color:"var(--ink2)"},
  btnSm:      {padding:"5px 10px",fontSize:12,borderRadius:8,cursor:"pointer",border:"1px solid var(--paper3)",background:"transparent",color:"var(--ink2)",fontFamily:"var(--mono)"},
  btnDanger:  {padding:"5px 10px",fontSize:12,borderRadius:8,cursor:"pointer",border:"1px solid var(--danger-l)",background:"transparent",color:"var(--danger)",fontFamily:"var(--mono)"},
  card:       {background:"#fff",borderRadius:10,boxShadow:"0 2px 8px rgba(0,0,0,0.08)",overflow:"hidden"},
  statCard:   (c)=>({background:"#fff",borderRadius:10,padding:"18px 20px",boxShadow:"0 2px 8px rgba(0,0,0,0.08)",borderLeft:`3px solid ${c}`}),
  th:         (r)=>({padding:"10px 16px",textAlign:r?"right":"left",fontSize:11,letterSpacing:"0.8px",textTransform:"uppercase",color:"var(--ink3)",fontWeight:500,whiteSpace:"nowrap"}),
  td:         {padding:"12px 16px",fontSize:13,color:"var(--ink)"},
  input:      {padding:"8px 12px",border:"1px solid var(--paper3)",borderRadius:8,fontFamily:"var(--mono)",fontSize:13,color:"var(--ink)",background:"var(--paper)",outline:"none",width:"100%"},
  label:      {fontSize:11,letterSpacing:"0.6px",textTransform:"uppercase",color:"var(--ink3)",fontWeight:500,display:"block",marginBottom:5},
  overlay:    {position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:20},
};

function AutoComplete({ value, onChange, onSelect, items, placeholder, renderItem, getKey, getLabel }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1); // keyboard-highlighted row
  const ref = useRef(null);
  const listRef = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const { list: filtered, total: matchTotal } = matchItems(items, value, getLabel, 50);
  const act = active < filtered.length ? active : -1;
  useEffect(() => {
    if (act >= 0 && listRef.current) listRef.current.children[act]?.scrollIntoView({ block: "nearest" });
  }, [act]);
  const pick = (item) => { onSelect(item); setOpen(false); setActive(-1); };
  // Arrow keys move through the suggestions, Enter picks the highlighted
  // one, Esc/Tab close the list, so entry never needs the mouse.
  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) { setOpen(true); setActive(filtered.length ? 0 : -1); }
      else if (filtered.length) setActive(a => (a + 1) % filtered.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (open && filtered.length) setActive(a => (a <= 0 ? filtered.length - 1 : a - 1));
    } else if (e.key === "Enter") {
      if (open && act >= 0 && filtered[act]) { e.preventDefault(); pick(filtered[act]); }
    } else if (e.key === "Escape") {
      if (open) { e.preventDefault(); setOpen(false); setActive(-1); }
    } else if (e.key === "Tab") {
      setOpen(false);
    }
  };
  return (
    <div style={{position:"relative"}} ref={ref}>
      <input value={value||""} onChange={e=>{onChange(e.target.value);setOpen(true);setActive(-1);}}
        onFocus={()=>setOpen(true)} onKeyDown={onKeyDown} placeholder={placeholder} style={S.input}/>
      {open&&filtered.length>0&&(
        <div ref={listRef} style={{position:"absolute",top:"100%",left:0,right:0,background:"#fff",border:"1px solid var(--paper3)",borderRadius:8,boxShadow:"0 8px 32px rgba(0,0,0,0.12)",zIndex:50,maxHeight:200,overflowY:"auto",marginTop:2}}>
          {filtered.map((item, idx)=>(
            <div key={getKey(item)} style={{padding:"9px 12px",fontSize:13,cursor:"pointer",borderBottom:"1px solid var(--paper2)",background:idx===act?"var(--paper2)":"transparent"}}
              onMouseDown={()=>pick(item)}>
              {renderItem(item)}
            </div>
          ))}
          {matchTotal > filtered.length && (
            <div style={{padding:"8px 12px",fontSize:11,color:"var(--ink3)"}}>Showing first {filtered.length} of {matchTotal} — keep typing to narrow</div>
          )}
        </div>
      )}
    </div>
  );
}

// Production/job-floor print slip — mirrors App.js's work order PrintView
// (same .print-area / @media print rules, defined globally there).
function DWPrintView({ record, lines, onClose }) {
  const rows = [
    ["Direct work",record.dw_number],["Date",fmtDMY(record.order_date)],
    ["Status",(record.status||"").replace("_"," ")],["Customer",record.customers?.name||"—"],
    ["Phone",record.customers?.phone||"—"],record.notes?["Notes",record.notes]:null,
  ].filter(Boolean);
  return (
    <div style={S.overlay} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:"#fff",borderRadius:14,width:"100%",maxWidth:640,maxHeight:"92vh",overflowY:"auto"}}>
        <div style={{padding:"20px 24px",display:"flex",alignItems:"center",justifyContent:"space-between",borderBottom:"1px solid var(--paper2)"}}>
          <div style={S.pageTitle}>Print preview — {record.dw_number}</div>
          <div style={{display:"flex",gap:8}}>
            <button style={S.btnPrimary} onClick={()=>window.print()}>🖨 Print (A5)</button>
            <button style={{background:"none",border:"none",cursor:"pointer",fontSize:19,color:"var(--ink3)"}} onClick={onClose}>✕</button>
          </div>
        </div>
        <div style={{padding:24}}>
          <div style={{border:"1px solid var(--paper3)",borderRadius:8,padding:24,background:"#fff",fontFamily:"var(--mono)",fontSize:13}}>
            <div style={{textAlign:"center",marginBottom:16,paddingBottom:12,borderBottom:"2px solid var(--ink)"}}>
              <div style={{fontFamily:"var(--serif)",fontSize:21,fontWeight:600}}>Super Foto DG X Press</div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:16}}>
              {rows.map(([lbl,val])=>(
                <div key={lbl}><div style={{fontSize:10,color:"var(--ink3)",textTransform:"uppercase",letterSpacing:"0.6px"}}>{lbl}</div><div style={{fontWeight:500}}>{val}</div></div>
              ))}
            </div>
            <table style={{width:"100%",borderCollapse:"collapse",marginBottom:12}}>
              <thead><tr style={{background:"var(--paper2)",borderBottom:"1px solid var(--paper3)"}}>
                <th style={{padding:"6px 8px",textAlign:"left",fontSize:10,textTransform:"uppercase",color:"var(--ink3)"}}>Product</th>
                <th style={{padding:"6px 8px",textAlign:"right",fontSize:10,textTransform:"uppercase",color:"var(--ink3)"}}>Qty</th>
              </tr></thead>
              <tbody>
                {lines.map((l,i)=>(
                  <tr key={i} style={{borderBottom:"1px solid var(--paper2)"}}>
                    <td style={{padding:"6px 8px",fontSize:12,fontWeight:500}}>{l.productName||"—"}</td>
                    <td style={{padding:"6px 8px",fontSize:12,textAlign:"right"}}>{l.quantity}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{borderTop:"1px solid var(--paper3)",paddingTop:8,fontSize:11,color:"var(--ink3)",textAlign:"center"}}>Super Foto DG X Press · Work Order System</div>
          </div>
          <div className="print-area" style={{fontFamily:"Arial,sans-serif",fontSize:"9pt",color:"#000"}}>
            <div style={{textAlign:"center",marginBottom:"3mm",paddingBottom:"2mm",borderBottom:"2pt solid #000"}}>
              <div style={{fontSize:"14pt",fontWeight:"bold"}}>Super Foto DG X Press</div>
            </div>
            {/* Same compact slip as the work order print: details on top, items below, no rates. */}
            <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:"1.5mm 3mm",marginBottom:"3mm",paddingBottom:"2.5mm",borderBottom:"1pt solid #000"}}>
              {[["Direct work",record.dw_number],["Date",fmtDMY(record.order_date)],["Status",(record.status||"").replace("_"," ")],["Phone",record.customers?.phone||"—"],
                ["Customer",record.customers?.name||"—",4],
                record.notes?["Notes",record.notes,4]:null].filter(Boolean).map(([lbl,val,span])=>(
                <div key={lbl} style={span?{gridColumn:`span ${span}`}:undefined}><div style={{fontSize:"6.5pt",color:"#333",fontWeight:"bold",textTransform:"uppercase",lineHeight:1.2}}>{lbl}</div><div style={{fontWeight:"bold",fontSize:"9.5pt",lineHeight:1.2}}>{val}</div></div>
              ))}
            </div>
            <table style={{width:"100%",borderCollapse:"collapse",marginBottom:"2mm"}}>
              <thead><tr style={{borderBottom:"1.25pt solid #000"}}>
                <th style={{padding:"1mm",textAlign:"left",fontSize:"8pt"}}>Product</th>
                <th style={{padding:"1mm",textAlign:"right",fontSize:"8pt"}}>Qty</th>
              </tr></thead>
              <tbody>
                {lines.map((l,i)=>(
                  <tr key={i} style={{borderBottom:"0.75pt solid #666",pageBreakInside:"avoid"}}>
                    <td style={{padding:"1.2mm 1mm",fontSize:"9pt",fontWeight:"bold"}}>{l.productName||"—"}</td>
                    <td style={{padding:"1.2mm 1mm",fontSize:"9pt",textAlign:"right"}}>{l.quantity}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{borderTop:"1pt solid #000",paddingTop:"1.5mm",fontSize:"7pt",color:"#333",textAlign:"center"}}>Super Foto DG X Press</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function DirectWorkModal({ record, onClose, onSave, toast, unlockInvoiced }) {
  const isEdit = !!record;
  const [customerQ, setCustomerQ]     = useState(record?.customers?.name||"");
  const [customer, setCustomer]       = useState(record?.customers||null);
  const [orderDate, setOrderDate]     = useState(record?.order_date||new Date().toISOString().slice(0,10));
  const [notes, setNotes]             = useState(record?.notes||"");
  const [lines, setLines]             = useState([]);
  const [customers, setCustomers]     = useState([]);
  const [products, setProducts]       = useState([]);
  const [saving, setSaving]           = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [custPrices, setCustPrices]   = useState({});
  const [printRecord, setPrintRecord] = useState(null);
  const [printLines, setPrintLines]   = useState([]);
  const [printThenClose, setPrintThenClose] = useState(false);

  useEffect(() => {
    Promise.all([
      fetchAll("customers",{select:"id,name,type,phone",eq:{is_active:true},order:"name"}),
      fetchAll("products",{select:"id,name,unit,base_price,tax_rate",eq:{is_active:true},order:"name"}),
    ]).then(([c,p]) => { setCustomers(c); setProducts(p); });

    if (record) {
      supabase.from("dw_line_items")
        .select("*, products(name,unit,base_price,tax_rate)")
        .eq("dw_order_id", record.id)
        .order("sort_order")
        .then(({data}) => {
          setLines((data||[]).map(l=>({
            id:          l.id,
            product_id:  l.product_id,
            productQ:    l.products?.name||l.description||"",
            productName: l.products?.name||l.description||"",
            description: l.description||"",
            quantity:    l.quantity,
            unit_price:  l.unit_price,
            tax_rate:    l.products?.tax_rate||0,
            unit:        l.products?.unit||"nos",
          })));
        });
      if (record.customer_id) loadCustPrices(record.customer_id);
    } else {
      setLines([{product_id:"",productQ:"",productName:"",description:"",quantity:1,unit_price:0,tax_rate:0,unit:"nos"}]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCustPrices = async (custId) => {
    const {data} = await supabase.from("customer_pricing")
      .select("product_id,agreed_price")
      .eq("customer_id", custId)
      .not("product_id","is",null);
    const map={};
    (data||[]).forEach(cp=>{ if(cp.product_id) map[cp.product_id]=parseFloat(cp.agreed_price); });
    setCustPrices(map);
  };

  const handleProductSelect = (product, lineIdx) => {
    const price = custPrices[product.id]!=null ? custPrices[product.id] : parseFloat(product.base_price)||0;
    const hasCustPrice = custPrices[product.id]!=null;
    const newLines = [...lines];
    newLines[lineIdx] = {
      ...newLines[lineIdx],
      product_id:  product.id,
      productName: product.name,
      productQ:    product.name,
      unit:        product.unit,
      tax_rate:    parseFloat(product.tax_rate)||0,
      unit_price:  price,
    };
    setLines(newLines);
    if (hasCustPrice) toast(`Special price for ${product.name}`,"info");
  };

  const addLine = () => setLines(l=>[...l,{product_id:"",productQ:"",productName:"",description:"",quantity:1,unit_price:0,tax_rate:0,unit:"nos"}]);
  const removeLine = (idx) => setLines(l=>l.filter((_,i)=>i!==idx));
  const updateLine = (idx,key,val) => setLines(l=>l.map((line,i)=>i===idx?{...line,[key]:val}:line));

  const grandTotal = lines.reduce((s,l)=>s+(parseFloat(l.quantity)||0)*(parseFloat(l.unit_price)||0),0);

  const saveCustPrice = async (lineIdx) => {
    const line = lines[lineIdx];
    if (!customer?.id || !line.product_id) return;
    const price = parseFloat(line.unit_price);
    if (!price) return;
    const {data:ex} = await supabase.from("customer_pricing")
      .select("id")
      .eq("customer_id", customer.id)
      .eq("product_id", line.product_id)
      .maybeSingle();
    if (ex?.id) {
      await supabase.from("customer_pricing")
        .update({agreed_price:price, updated_at:new Date().toISOString()})
        .eq("id", ex.id);
    } else {
      await supabase.from("customer_pricing")
        .insert({customer_id:customer.id, product_id:line.product_id, agreed_price:price, updated_at:new Date().toISOString()});
    }
    toast("Special price saved!");
    setCustPrices(p=>({...p,[line.product_id]:price}));
  };

  const openPrint = () => {
    setPrintLines(lines.map(l=>({productName:l.productName||l.productQ||l.description||"—",quantity:l.quantity,unit_price:l.unit_price})));
    setPrintRecord(record);
  };

const save = async (status, alsoPrint=false) => {
  if (!customer?.id) return alert("Please select a customer.");
  if (lines.length===0) return alert("Add at least one line item.");
  if (isEdit && record.status==="invoiced" && !unlockInvoiced) return alert("This direct work order is already invoiced — edit it from the invoice instead.");
  // Editing through the invoice must not un-invoice the order.
  if (isEdit && unlockInvoiced && record.status==="invoiced") status = "invoiced";
  setSaving(true);
 let dwNumber = record?.dw_number;
 try {
  if (!isEdit) {

    const {data:numData, error:numErr} = await supabase.rpc("next_dw_number");
    if (numErr) throw numErr;
    dwNumber = String(numData);


    const {data:d, error:insertErr} = await supabase
      .from("direct_work_orders")
      .insert({
        dw_number:   dwNumber,
        customer_id: customer.id,
        status:      status,
        notes:       notes||null,
        order_date:  orderDate,
      })
      .select("id")
      .single();
    if (insertErr) throw insertErr;


    const {error:lineErr} = await supabase
      .from("dw_line_items")
      .insert(lines.map((l,i)=>({
        dw_order_id: d.id,
        product_id:  l.product_id||null,
        description: l.productName||l.description||null,
        quantity:    parseFloat(l.quantity)||1,
        unit_price:  parseFloat(l.unit_price)||0,
        sort_order:  i,
      })));
    if (lineErr) throw lineErr;


  } else {

    const {error:updateErr} = await supabase
      .from("direct_work_orders")
      .update({
        customer_id: customer.id,
        status:      status,
        notes:       notes||null,
        order_date:  orderDate,
        updated_at:  new Date().toISOString(),
      })
      .eq("id", record.id);
    if (updateErr) throw updateErr;


    const {error:delErr} = await supabase
      .from("dw_line_items")
      .delete()
      .eq("dw_order_id", record.id);
    if (delErr) throw delErr;


    const {error:lineErr} = await supabase
      .from("dw_line_items")
      .insert(lines.map((l,i)=>({
        dw_order_id: record.id,
        product_id:  l.product_id||null,
        description: l.productName||l.description||null,
        quantity:    parseFloat(l.quantity)||1,
        unit_price:  parseFloat(l.unit_price)||0,
        sort_order:  i,
      })));
    if (lineErr) throw lineErr;

  }

  if (alsoPrint) {
    setPrintLines(lines.map(l=>({productName:l.productName||l.productQ||l.description||"—",quantity:l.quantity,unit_price:l.unit_price})));
    setPrintRecord({dw_number:dwNumber, order_date:orderDate, status, customers:{name:customer?.name, phone:customer?.phone}, notes});
    setPrintThenClose(true);
  } else {
    onSave();
  }
} catch(e) {
alert("Error: " + e.message);
}finally {
      setSaving(false);
    }
  };

  const deleteDW = async () => {
    try {
      const {error:delLines} = await supabase
        .from("dw_line_items")
        .delete()
        .eq("dw_order_id", record.id);
      if (delLines) throw delLines;

      const {error:delOrder} = await supabase
        .from("direct_work_orders")
        .update({is_deleted:true, updated_at:new Date().toISOString()})
        .eq("id", record.id);
      if (delOrder) throw delOrder;

      toast(`${record.dw_number} deleted.`,"error");
      onSave();
    } catch(e) {
      alert("Error deleting: "+e.message);
    }
  };

  const COL = "1fr 80px 110px 90px 32px";

  return (
    <div style={S.overlay} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:"#fff",borderRadius:14,boxShadow:"0 8px 32px rgba(0,0,0,0.12)",width:"100%",maxWidth:780,maxHeight:"92vh",overflowY:"auto"}}>
        <div style={{padding:"24px 28px 0",display:"flex",alignItems:"center",justifyContent:"space-between",position:"sticky",top:0,background:"#fff",zIndex:10,paddingBottom:16,borderBottom:"1px solid var(--paper2)"}}>
          <div style={S.pageTitle}>{isEdit?`Edit ${record.dw_number}`:"New direct work"}</div>
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            {isEdit&&<button style={S.btnGhost} onClick={openPrint}>🖨 Print</button>}
            <button style={{background:"none",border:"none",cursor:"pointer",fontSize:19,color:"var(--ink3)"}} onClick={onClose}>✕</button>
          </div>
        </div>
        <div style={{padding:"20px 28px 28px"}}>

          {isEdit&&record.status==="invoiced"&&!unlockInvoiced&&(
            <div style={{marginBottom:16,padding:"12px 16px",background:"var(--warn-l)",borderRadius:8,fontSize:13,color:"var(--warn)"}}>
              ⚠ This order is already invoiced — changes here won't save. Edit it from the invoice instead.
            </div>
          )}

          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:20}}>
            <div style={{gridColumn:"span 2"}}>
              <label style={S.label}>Customer *</label>
              <AutoComplete
                value={customerQ}
                onChange={v=>{setCustomerQ(v);if(!v){setCustomer(null);setCustPrices({});}}}
                onSelect={c=>{setCustomer(c);setCustomerQ(c.name);loadCustPrices(c.id);}}
                items={customers} placeholder="Type to search customers…"
                getKey={c=>c.id} getLabel={c=>c.name}
                renderItem={c=>(
                  <div>
                    <div style={{fontWeight:500}}>{c.name}</div>
                    <div style={{fontSize:11,color:"var(--ink3)"}}>{c.type}{c.phone?" · "+c.phone:""}</div>
                  </div>
                )}
              />
            </div>
            <div>
              <label style={S.label}>Date</label>
              <input type="date" value={orderDate} onChange={e=>setOrderDate(e.target.value)} style={S.input}/>
            </div>
            <div>
              <label style={S.label}>Notes</label>
              <input value={notes} onChange={e=>setNotes(e.target.value)}
                placeholder="What is this for? e.g. 1 bag, 2 prints…" style={S.input}/>
            </div>
          </div>

          <label style={S.label}>Line items *</label>
          <div style={{border:"1px solid var(--paper3)",borderRadius:10,overflow:"hidden",marginTop:4}}>
            <div style={{display:"grid",gridTemplateColumns:COL,gap:8,padding:"8px 12px",background:"var(--paper2)",fontSize:11,letterSpacing:"0.6px",textTransform:"uppercase",color:"var(--ink3)"}}>
              <div>Product / description</div>
              <div>Qty</div>
              <div>Unit price (₹)</div>
              <div style={{textAlign:"right"}}>Total</div>
              <div></div>
            </div>

            {lines.map((line,idx)=>(
              <div key={idx} style={{display:"grid",gridTemplateColumns:COL,gap:8,padding:"8px 12px",borderTop:"1px solid var(--paper2)",alignItems:"center"}}>
                <div>
                  <AutoComplete
                    value={line.productQ||""}
                    onChange={v=>updateLine(idx,"productQ",v)}
                    onSelect={p=>handleProductSelect(p,idx)}
                    items={products}
                    placeholder="Search product…"
                    getKey={p=>p.id} getLabel={p=>p.name}
                    renderItem={p=>(
                      <div>
                        <div style={{fontWeight:500}}>{p.name}</div>
                        <div style={{fontSize:11,color:"var(--ink3)"}}>{fmt(p.base_price)} / {p.unit}</div>
                      </div>
                    )}
                  />
                  {line.product_id&&customer?.id&&(
                    <div style={{marginTop:4,display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
                      {custPrices[line.product_id]!=null&&(
                        <span style={{display:"inline-block",padding:"1px 6px",borderRadius:6,fontSize:11,background:"var(--accent-l)",color:"var(--accent)",fontWeight:500}}>★ Special price</span>
                      )}
                      <button style={{...S.btnSm,fontSize:11,padding:"2px 7px"}} onClick={()=>saveCustPrice(idx)}>★ Save as special</button>
                    </div>
                  )}
                </div>
                <input type="number" min="0.001" step="any" value={line.quantity}
                  onChange={e=>updateLine(idx,"quantity",e.target.value)}
                  style={{...S.input,padding:"6px 8px",fontSize:13}}/>
                <input type="number" min="0" step="0.01" value={line.unit_price}
                  onChange={e=>updateLine(idx,"unit_price",e.target.value)}
                  style={{...S.input,padding:"6px 8px",fontSize:13}}/>
                <div style={{fontFamily:"var(--mono)",fontWeight:500,fontSize:13,textAlign:"right"}}>
                  {fmt((parseFloat(line.quantity)||0)*(parseFloat(line.unit_price)||0))}
                </div>
                <button style={{...S.btnDanger,padding:"4px 8px"}} onClick={()=>removeLine(idx)}>✕</button>
              </div>
            ))}

            <div
              style={{display:"flex",alignItems:"center",gap:8,padding:"10px 12px",cursor:"pointer",color:"var(--accent)",fontSize:13,fontFamily:"var(--mono)",borderTop:"1px solid var(--paper2)",background:"var(--paper)"}}
              onClick={addLine}>
              <span style={{fontSize:17}}>+</span> Add line item
            </div>
          </div>

          {lines.length>0&&(
            <div style={{marginTop:16,background:"var(--paper)",borderRadius:10,padding:"16px 20px",border:"1px solid var(--paper3)"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:16,fontWeight:500,fontFamily:"var(--serif)"}}>
                <span>Order total</span>
                <span>{fmt(grandTotal)}</span>
              </div>
            </div>
          )}

          {isEdit&&confirmDelete&&(
            <div style={{background:"var(--danger-l)",border:"1px solid var(--danger)",borderRadius:10,padding:"16px 20px",marginTop:16}}>
              <div style={{fontWeight:500,color:"var(--danger)",marginBottom:8,fontSize:14}}>
                ⚠ Confirm deletion of {record?.dw_number}
              </div>
              <div style={{fontSize:13,color:"var(--ink2)",marginBottom:14}}>
                This will permanently remove the direct work order and all its line items.
              </div>
              <div style={{display:"flex",gap:10}}>
                <button style={S.btnSm} onClick={()=>setConfirmDelete(false)}>Cancel</button>
                <button style={{...S.btnDanger,padding:"6px 14px"}} onClick={deleteDW}>
                  Yes, delete {record?.dw_number}
                </button>
              </div>
            </div>
          )}

          <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:24,paddingTop:20,borderTop:"1px solid var(--paper2)"}}>
            {isEdit&&(record.status!=="invoiced"||unlockInvoiced)&&(
              <button style={{...S.btnDanger,marginRight:"auto"}} onClick={()=>setConfirmDelete(true)}>
                Delete
              </button>
            )}
            <button style={S.btnGhost} onClick={onClose}>Cancel</button>
            <button style={S.btnGhost} onClick={()=>save("open")} disabled={saving}>
              Save as open
            </button>
            <button style={S.btnGhost} onClick={()=>save("in_progress",true)} disabled={saving}>
              {saving ? "Saving…" : "🖨 Save & print"}
            </button>
            <button style={S.btnPrimary} onClick={()=>save("in_progress")} disabled={saving}>
              {saving ? "Saving…" : "🛒 Save & start"}
            </button>
          </div>
        </div>
      </div>
      {printRecord&&<DWPrintView record={printRecord} lines={printLines} onClose={()=>{
        setPrintRecord(null); setPrintLines([]);
        if (printThenClose) { setPrintThenClose(false); onSave(); }
      }}/>}
    </div>
  );
}

export function DirectWorkPage({ toast }) {
  const [orders, setOrders]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [modal, setModal]       = useState(null);
  const [search, setSearch]     = useState("");
  const [filter, setFilter]     = useState("all");
  const [fromDate, setFromDate] = useState(""); // "" until the default (most recent) date is known
  const [toDate, setToDate]     = useState("");
  const [allDates, setAllDates] = useState(false);
  const [defaultDate, setDefaultDate] = useState("");

  // Scoped to [fromDate,toDate] (both null when allDates is set) and
  // fetched in full via pagination — same pattern as Sales/Payments, so
  // this no longer silently loads every direct work order ever created.
  const load = useCallback(async () => {
    if (!allDates && !(fromDate && toDate)) return; // scope not yet known
    setLoading(true);
    let all = [], from = 0;
    const pageSize = 1000;
    while (true) {
      let q = supabase.from("direct_work_orders").select("*, customers(id,name,type,phone)").eq("is_deleted", false);
      if (!allDates && fromDate && toDate) q = q.gte("order_date", fromDate).lte("order_date", toDate);
      const { data, error } = await q.order("created_at", { ascending: false }).range(from, from + pageSize - 1);
      if (error || !data || data.length === 0) break;
      all = [...all, ...data];
      if (data.length < pageSize) break;
      from += pageSize;
    }
    setOrders(all); setLoading(false);
  }, [fromDate, toDate, allDates]);

  // Default-scope the first load to the most recent order date instead of
  // loading everything on every visit.
  useEffect(() => {
    (async () => {
      const { data } = await supabase.from("direct_work_orders")
        .select("order_date").eq("is_deleted", false).order("order_date", { ascending: false }).limit(1);
      const latest = (data && data[0]) ? data[0].order_date : new Date().toISOString().slice(0, 10);
      setDefaultDate(latest);
      setFromDate(latest); setToDate(latest);
    })();
  }, []);

  useEffect(()=>{load();},[load]);

  const showAllDates = () => { setAllDates(true); setFromDate(""); setToDate(""); };
  const setFromDateScoped = (v) => { setAllDates(false); setFromDate(v); };
  const setToDateScoped   = (v) => { setAllDates(false); setToDate(v); };
  const resetToDefault = () => {
    setSearch(""); setFilter("all"); setAllDates(false);
    setFromDate(defaultDate); setToDate(defaultDate);
  };

  const updateStatus = async (id, status) => {
    await supabase.from("direct_work_orders")
      .update({status, updated_at:new Date().toISOString()})
      .eq("id", id);
    toast(`Status updated to ${status.replace("_"," ")}.`);
    load();
  };

  // Date range is already applied server-side by load() — only search and
  // status are filtered client-side here.
  const filtered = orders.filter(o=>{
    const s = search.toLowerCase();
    return (
      ((o.dw_number||"").toLowerCase().includes(s)||
       (o.customers?.name||"").toLowerCase().includes(s)||
       (o.notes||"").toLowerCase().includes(s)) &&
      (filter==="all"||o.status===filter)
    );
  });

  const downloadCSV = () => {
    const headers = ["DW Number","Customer","Type","Date","Notes","Status"];
    const rows = filtered.map(o=>[
      o.dw_number||"", o.customers?.name||"", o.customers?.type||"",
      fmtDMY(o.order_date), o.notes||"", (o.status||"").replace("_"," "),
    ]);
    const csv = [headers,...rows].map(r=>r.map(v=>`"${String(v||"").replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob = new Blob([csv],{type:"text/csv;charset=utf-8;"});
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href=url; link.download=`direct-work-${new Date().toISOString().slice(0,10)}.csv`; link.click();
    URL.revokeObjectURL(url);
  };

  // Pagination — "Show all dates" can pull in a lot of rows, which
  // shouldn't all render at once.
  const PAGE_SIZE = 50;
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [search, filter, orders]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageRows = filtered.slice((currentPage-1)*PAGE_SIZE, currentPage*PAGE_SIZE);

  const counts = {
    open:        orders.filter(o=>o.status==="open").length,
    in_progress: orders.filter(o=>o.status==="in_progress").length,
    completed:   orders.filter(o=>o.status==="completed").length,
  };

  return (
    <div>
      <div style={S.pageHeader}>
        <div>
          <div style={S.pageTitle}>Direct Work</div>
          <div style={S.subtitle}>
            Quick sales · bags · prints · ad-hoc items · {orders.length} total
            {allDates ? " · all dates" : fromDate ? (fromDate===toDate ? ` · ${fmtDMY(fromDate)}` : ` · ${fmtDMY(fromDate)} – ${fmtDMY(toDate)}`) : ""}
          </div>
        </div>
        <div style={{display:"flex",gap:8}}>
          <button style={S.btnGhost} onClick={downloadCSV}>⬇ CSV</button>
          <button style={S.btnPrimary} onClick={()=>setModal("new")}>+ New direct work</button>
        </div>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:16,marginBottom:28}}>
        {[
          {label:"Open",        value:counts.open,        color:"var(--info)"},
          {label:"In progress", value:counts.in_progress, color:"var(--warn)"},
          {label:"Completed",   value:counts.completed,   color:"var(--accent)"},
          {label:"Total",       value:orders.length,      color:"#6a1b9a"},
        ].map(s=>(
          <div key={s.label} style={S.statCard(s.color)}>
            <div style={{fontFamily:"var(--serif)",fontSize:29,fontWeight:300,lineHeight:1}}>{s.value}</div>
            <div style={{fontSize:11,color:"var(--ink3)",letterSpacing:"0.6px",textTransform:"uppercase",marginTop:6}}>{s.label}</div>
          </div>
        ))}
      </div>

      <div style={{background:"#fff",border:"1px solid var(--paper3)",borderRadius:10,padding:"16px 20px",marginBottom:20,boxShadow:"0 2px 8px rgba(0,0,0,0.08)"}}>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:12}}>
          <div>
            <label style={S.label}>Search</label>
            <div style={{position:"relative"}}>
              <span style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",color:"var(--ink3)",fontSize:14,pointerEvents:"none"}}>🔍</span>
              <input placeholder="DW number, customer, notes…" value={search} onChange={e=>setSearch(e.target.value)}
                style={{...S.input,paddingLeft:34,background:"#fff"}}/>
            </div>
          </div>
          <div>
            <label style={S.label}>From date</label>
            <input type="date" value={fromDate} onChange={e=>setFromDateScoped(e.target.value)} style={S.input}/>
          </div>
          <div>
            <label style={S.label}>To date</label>
            <input type="date" value={toDate} onChange={e=>setToDateScoped(e.target.value)} style={S.input}/>
          </div>
        </div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
          {["all","open","in_progress","completed","invoiced"].map(f=>(
            <button key={f} onClick={()=>setFilter(f)}
              style={{padding:"6px 12px",borderRadius:20,fontSize:12,cursor:"pointer",
                border:"1px solid var(--paper3)",fontFamily:"var(--mono)",
                background:filter===f?"var(--accent)":"#fff",
                color:filter===f?"#fff":"var(--ink2)"}}>
              {f==="all"?"All":f==="in_progress"?"In progress":f.charAt(0).toUpperCase()+f.slice(1)}
            </button>
          ))}
          {!allDates&&<button style={S.btnSm} onClick={showAllDates}>⬆ Show all dates</button>}
          {(search||filter!=="all"||allDates||fromDate!==defaultDate||toDate!==defaultDate)&&(
            <button style={S.btnSm} onClick={resetToDefault}>✕ Reset to {defaultDate?fmtDMY(defaultDate):"latest"}</button>
          )}
          <span style={{fontSize:12,color:"var(--ink3)",marginLeft:"auto"}}>{filtered.length} order{filtered.length!==1?"s":""}</span>
        </div>
      </div>

      <div style={S.card}>
        <table style={{width:"100%",borderCollapse:"collapse"}}>
          <thead>
            <tr style={{background:"var(--paper2)",borderBottom:"1px solid var(--paper3)"}}>
              <th style={S.th()}>DW number</th>
              <th style={S.th()}>Customer</th>
              <th style={S.th()}>Date</th>
              <th style={S.th()}>Notes</th>
              <th style={S.th()}>Status</th>
              <th style={S.th()}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading&&<tr><td colSpan={6} style={{padding:40,textAlign:"center",color:"var(--ink3)"}}>Loading…</td></tr>}
            {!loading&&filtered.length===0&&(
              <tr><td colSpan={6}>
                <div style={{textAlign:"center",padding:"60px 20px",color:"var(--ink3)"}}>
                  <div style={{fontSize:37,marginBottom:12,opacity:0.5}}>🛒</div>
                  <p style={{fontSize:14}}>No direct work orders yet.</p>
                </div>
              </td></tr>
            )}
            {!loading&&pageRows.map(o=>(
              <tr key={o.id} style={{borderBottom:"1px solid var(--paper2)"}}>
                <td style={S.td}>
                  <span style={{fontFamily:"var(--mono)",fontSize:12,fontWeight:500,background:"var(--paper2)",padding:"3px 8px",borderRadius:6,color:"var(--ink2)"}}>
                    {o.dw_number||"—"}
                  </span>
                </td>
                <td style={S.td}>
                  <div style={{fontWeight:500}}>{o.customers?.name||"—"}</div>
                  <div style={{fontSize:11,color:"var(--ink3)"}}>{o.customers?.type}</div>
                </td>
                <td style={{...S.td,fontFamily:"var(--mono)",fontSize:12,color:"var(--ink2)"}}>{fmtDMY(o.order_date)}</td>
                <td style={{...S.td,fontSize:12,color:"var(--ink3)",maxWidth:200,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                  {o.notes||"—"}
                </td>
                <td style={S.td}>
                  <span style={{display:"inline-block",padding:"2px 8px",borderRadius:12,fontSize:11,fontWeight:500,
                    background:
                      o.status==="open"       ?"var(--info-l)":
                      o.status==="in_progress"?"var(--warn-l)":
                      o.status==="completed"  ?"var(--accent-l)":
                      o.status==="invoiced"   ?"#f3e8ff":
                      "var(--danger-l)",
                    color:
                      o.status==="open"       ?"var(--info)":
                      o.status==="in_progress"?"var(--warn)":
                      o.status==="completed"  ?"var(--accent)":
                      o.status==="invoiced"   ?"#6a1b9a":
                      "var(--danger)"}}>
                    {o.status?.replace("_"," ")}
                  </span>
                </td>
                <td style={S.td}>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                    <button style={S.btnSm} onClick={()=>setModal(o)}>{o.status==="invoiced"?"View":"Edit"}</button>
                    {o.status==="open"&&(
                      <button style={S.btnSm} onClick={()=>updateStatus(o.id,"in_progress")}>▶ Start</button>
                    )}
                    {o.status==="in_progress"&&(
                      <button style={{...S.btnSm,background:"var(--accent)",color:"#fff",border:"none"}}
                        onClick={()=>updateStatus(o.id,"completed")}>✓ Complete</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!loading&&filtered.length>PAGE_SIZE&&(
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:14,padding:"0 4px"}}>
          <span style={{fontSize:12,color:"var(--ink3)"}}>
            Showing {(currentPage-1)*PAGE_SIZE+1}–{Math.min(currentPage*PAGE_SIZE,filtered.length)} of {filtered.length}
          </span>
          <div style={{display:"flex",gap:6,alignItems:"center"}}>
            <button style={{...S.btnSm,opacity:currentPage<=1?0.4:1,cursor:currentPage<=1?"default":"pointer"}} disabled={currentPage<=1} onClick={()=>setPage(p=>Math.max(1,p-1))}>← Prev</button>
            <span style={{fontSize:12,color:"var(--ink3)",fontFamily:"var(--mono)"}}>Page {currentPage} of {totalPages}</span>
            <button style={{...S.btnSm,opacity:currentPage>=totalPages?0.4:1,cursor:currentPage>=totalPages?"default":"pointer"}} disabled={currentPage>=totalPages} onClick={()=>setPage(p=>Math.min(totalPages,p+1))}>Next →</button>
          </div>
        </div>
      )}

      {modal&&(
        <DirectWorkModal
          record={modal==="new"?null:modal}
          onClose={()=>setModal(null)}
          onSave={()=>{
            setModal(null);
            load();
            toast(modal==="new"?"Direct work order created!":"Updated!");
          }}
          toast={toast}
        />
      )}
    </div>
  );
}
